/**
 * ComprehensiveExportButton Component
 * Exports a comprehensive PDF report based on user role
 *
 * - Employee: My Performance only
 * - Manager: My Performance + Team Performance
 * - Admin: My Performance + Team Performance + Organization Performance
 */

import React, { useState } from 'react';
import { clsx } from 'clsx';
import { FileDown, Loader2, Check, AlertCircle, ChevronDown } from 'lucide-react';
import {
  generateComprehensivePDF,
  generateIndividualPDF,
  generateTeamPDF,
  generateOrgPDF,
  downloadBlob,
  generatePDFFilename,
  type ComprehensiveExportData,
} from '../../utils/performancePDF';
import { usePerformancePermissions } from '../../hooks/usePerformancePermissions';
import { useAuth } from '../../context/AuthContext';
import type {
  PerformanceData,
  TeamPerformanceSummary,
  OrganizationPerformanceSummary,
  PeriodType,
} from '../../types';

export type ExportScope = 'all' | 'individual' | 'team' | 'organization';

export interface ComprehensiveExportButtonProps {
  /** Individual performance data */
  individualData?: PerformanceData | null;
  /** Team performance data (for managers and admins) */
  teamData?: TeamPerformanceSummary | null;
  /** Organization performance data (for admins only) */
  organizationData?: OrganizationPerformanceSummary | null;
  /** Current period type */
  periodType: PeriodType;
  /** User's name for filename */
  userName?: string;
  /** Whether data is still loading */
  isLoading?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Button variant */
  variant?: 'primary' | 'secondary' | 'ghost';
  /** Button size */
  size?: 'sm' | 'md' | 'lg';
  /** Show dropdown menu for export options */
  showDropdown?: boolean;
}

export const ComprehensiveExportButton: React.FC<ComprehensiveExportButtonProps> = ({
  individualData,
  teamData,
  organizationData,
  periodType,
  userName,
  isLoading = false,
  className,
  variant = 'secondary',
  size = 'md',
  showDropdown = true,
}) => {
  const { user } = useAuth();
  const permissions = usePerformancePermissions();
  const [isExporting, setIsExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Don't render if user can't export
  if (!permissions.canExport) {
    return null;
  }

  // Get tenant name from organization data or user's organization name
  const tenantName = organizationData?.tenantName || user?.organizationName;

  // Check what data is available
  const hasIndividual = !!individualData;
  const hasTeam = !!teamData && permissions.canViewTeam;
  const hasOrg = !!organizationData && permissions.canViewOrg;
  const hasAnyData = hasIndividual || hasTeam || hasOrg;

  // Determine available export options based on role and data
  const allOptions: { scope: ExportScope; label: string; description: string; available: boolean }[] = [
    {
      scope: 'all' as ExportScope,
      label: 'Complete Report',
      description: hasOrg
        ? 'My Performance + Team + Organization'
        : hasTeam
        ? 'My Performance + Team'
        : 'My Performance',
      available: hasAnyData,
    },
    {
      scope: 'individual' as ExportScope,
      label: 'My Performance Only',
      description: 'Personal performance metrics',
      available: hasIndividual,
    },
    {
      scope: 'team' as ExportScope,
      label: 'Team Performance Only',
      description: 'Team metrics and direct reports',
      available: hasTeam,
    },
    {
      scope: 'organization' as ExportScope,
      label: 'Organization Only',
      description: 'Org-wide metrics and departments',
      available: hasOrg,
    },
  ];
  const exportOptions = allOptions.filter((opt) => opt.available);

  const handleExport = async (scope: ExportScope) => {
    setIsDropdownOpen(false);
    setIsExporting(true);
    setExportStatus('idle');

    try {
      let blob: Blob;
      let filename: string;

      if (scope === 'all') {
        // Comprehensive export with all available sections
        const exportData: ComprehensiveExportData = {
          individual: hasIndividual ? individualData : null,
          team: hasTeam ? teamData : null,
          organization: hasOrg ? organizationData : null,
        };

        blob = await generateComprehensivePDF(exportData, periodType, {
          includeTableOfContents: exportOptions.length > 2,
          tenantName,
        });
        filename = generatePDFFilename('comprehensive', periodType, userName);
      } else if (scope === 'individual' && individualData) {
        blob = await generateIndividualPDF(individualData, null, { tenantName });
        filename = generatePDFFilename('individual', periodType, userName);
      } else if (scope === 'team' && teamData) {
        blob = await generateTeamPDF(teamData, { tenantName });
        filename = generatePDFFilename('team', periodType);
      } else if (scope === 'organization' && organizationData) {
        blob = await generateOrgPDF(organizationData, { tenantName });
        filename = generatePDFFilename('organization', periodType);
      } else {
        throw new Error('No data available for export');
      }

      downloadBlob(blob, filename);
      setExportStatus('success');
      setTimeout(() => setExportStatus('idle'), 2000);
    } catch (error) {
      console.error('PDF export failed:', error);
      setExportStatus('error');
      setTimeout(() => setExportStatus('idle'), 3000);
    } finally {
      setIsExporting(false);
    }
  };

  const isDisabled = isLoading || !hasAnyData || isExporting;

  const buttonClasses = clsx(
    'inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-all relative',
    {
      // Variants
      'bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2':
        variant === 'primary' && !isDisabled,
      'bg-stone-100 text-stone-700 hover:bg-stone-200 focus:ring-2 focus:ring-stone-500 focus:ring-offset-2':
        variant === 'secondary' && !isDisabled,
      'text-stone-600 hover:text-stone-800 hover:bg-stone-100':
        variant === 'ghost' && !isDisabled,

      // Disabled state
      'opacity-50 cursor-not-allowed': isDisabled,

      // Success state
      'bg-green-100 text-green-700': exportStatus === 'success',

      // Error state
      'bg-red-100 text-red-700': exportStatus === 'error',

      // Sizes
      'px-3 py-1.5 text-sm': size === 'sm',
      'px-4 py-2 text-sm': size === 'md',
      'px-5 py-2.5 text-base': size === 'lg',
    },
    className
  );

  const getIcon = () => {
    if (isExporting) {
      return <Loader2 className="w-4 h-4 animate-spin" />;
    }
    if (exportStatus === 'success') {
      return <Check className="w-4 h-4" />;
    }
    if (exportStatus === 'error') {
      return <AlertCircle className="w-4 h-4" />;
    }
    return <FileDown className="w-4 h-4" />;
  };

  const getLabel = () => {
    if (isExporting) return 'Exporting...';
    if (exportStatus === 'success') return 'Exported!';
    if (exportStatus === 'error') return 'Export Failed';
    return 'Export PDF';
  };

  // Simple button for single option, dropdown for multiple options
  if (!showDropdown || exportOptions.length <= 1) {
    return (
      <button
        onClick={() => handleExport('all')}
        disabled={isDisabled}
        className={buttonClasses}
        title={isDisabled ? 'No data to export' : 'Export as PDF'}
      >
        {getIcon()}
        <span>{getLabel()}</span>
      </button>
    );
  }

  // Dropdown button for multiple export options
  return (
    <div className="relative">
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        disabled={isDisabled}
        className={buttonClasses}
        title={isDisabled ? 'No data to export' : 'Export as PDF'}
      >
        {getIcon()}
        <span>{getLabel()}</span>
        <ChevronDown
          className={clsx('w-4 h-4 transition-transform', {
            'rotate-180': isDropdownOpen,
          })}
        />
      </button>

      {/* Dropdown Menu */}
      {isDropdownOpen && !isDisabled && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsDropdownOpen(false)}
          />

          {/* Menu */}
          <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-stone-200 z-20 overflow-hidden">
            <div className="py-1">
              {exportOptions.map((option) => (
                <button
                  key={option.scope}
                  onClick={() => handleExport(option.scope)}
                  className="w-full px-4 py-3 text-left hover:bg-stone-50 transition-colors border-b border-stone-100 last:border-b-0"
                >
                  <div className="font-medium text-stone-800 text-sm">
                    {option.label}
                  </div>
                  <div className="text-xs text-stone-500 mt-0.5">
                    {option.description}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ComprehensiveExportButton;
